import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { InvestorSummary } from '@/api/auth';

interface AuthState {
  token: string | null;
  investor: InvestorSummary | null;
  setSession: (token: string, investor: InvestorSummary) => void;
  clear: () => void;
}

/**
 * Auth state for investors.nurev.io. Persists to localStorage so a
 * page reload keeps the session — the server-issued JWT is good for
 * 7 days, and if it expires the 401 interceptor in client.ts calls
 * clear() and the router redirects to /login.
 *
 * Unlike the admin/inference/fleet consoles which use magic-link auth,
 * the investor portal reuses the existing accept-invite + password
 * flow (admin emails an invite token, investor sets password on first
 * acceptance, then logs in with email + password thereafter). This is
 * intentional v1 — backend supports it today, no new endpoints needed.
 *
 * Separate localStorage key from the other consoles so all four
 * surfaces can coexist on the same machine without overwriting each
 * other's sessions.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    set => ({
      token: null,
      investor: null,
      setSession: (token, investor) => set({ token, investor }),
      clear: () => set({ token: null, investor: null })
    }),
    { name: 'nurev-investor-auth' }
  )
);

export const isAuthenticated = (): boolean => !!useAuthStore.getState().token;
